import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";

import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { IconButton } from "@/components/Button/IconButton";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { LinkExternal01Icon } from "@/components/Icons/LinkExternal01Icon";
import { MessageSquare02Icon } from "@/components/Icons/MessageSquare02Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { RichTextView } from "@/components/RichTextView/RichTextView";
import { Badge } from "@/design-system/Badge";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { integrationIsNew } from "@/modules/portfolio/helpers/integration";
import { useConfig } from "@/providers/config/useConfig";
import { usePortfolioQueries } from "@/queries/portfolio/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import { RuntimeBackgroundImage } from "@/ui-adapters/RuntimeBackgroundImage";

import bgPlaceholder from "./header-placeholder.jpg";

export function AvailableIntegrationDetailPage(): React.ReactNode {
  const { t } = useTranslation();
  const { id } = useParams<{
    id: string;
  }>();
  const integrationId = id ?? "";
  const sessionUser = useSessionUser();
  const preprUrl = useConfig("preprDashboardUrl");
  const portfolioQueries = usePortfolioQueries();
  const { data, isLoading, error } = useQuery({
    ...portfolioQueries.availableIntegrationDetail(integrationId, sessionUser.language.id, preprUrl),
    enabled: Boolean(id),
  });
  if (error) {
    return <ErrorPage error={error} />;
  }
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }
  const integration = data?.data.Integration;

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={integration?.name || ""}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.portfolio.title"),
            },
            {
              name: t("page.available-integrations.title"),
              to: routes.portfolio.integrations(),
            },
            {
              name: integration?.name || "",
            },
          ]}
        />
      }
    >
      <PageGrid.Row>
        <PageGrid.Column size="50%">
          <Card as="article" density="flush" clip>
            <header className="relative">
              <RuntimeBackgroundImage
                imageUrl={integration?.header_image ? integration.header_image.url : bgPlaceholder}
                presentation="banner"
              />
              <img
                className="absolute right-space-xl -bottom-space-xl size-illustration-compact rounded-full bg-background-default object-cover p-space-xs"
                src={integration?.profile_image?.url}
                alt=""
              />
            </header>
            <Box padding="md">
              <Stack gap="md">
                <span className="text-heading-3">
                  <Inline gap="md" align="center" as="span">
                    <span>{integration?.name}</span>

                    {integrationIsNew(integration?.created) && (
                      <Badge variant="success">{t("page.available-integrations.integration.new")}</Badge>
                    )}
                  </Inline>
                </span>
                <Box padding={{ y: "sm" }}>
                  <Spread space="md">
                    <p className="text-body-emphasis">{t("page.available-integrations.integration.contact")}</p>
                    <IconButton
                      icon={<MessageSquare02Icon />}
                      onClick={() =>
                        window.Intercom(
                          "showNewMessage",
                          t("page.available-integrations.integration.contact.default-message", {
                            integrationName: integration?.name || "",
                          }),
                        )
                      }
                      title={t("page.available-integrations.integration.contact")}
                      isCircular
                    />
                  </Spread>
                </Box>
                {integration?.content && <RichTextView>{integration.content}</RichTextView>}
                {integration?.external_link ? (
                  <Button
                    variant="ghostSecondary"
                    size="sm"
                    type="link"
                    href={integration.external_link}
                    icon={<LinkExternal01Icon />}
                    isExternal
                  >
                    {integration.external_link}
                  </Button>
                ) : null}
              </Stack>
            </Box>
          </Card>
        </PageGrid.Column>
      </PageGrid.Row>
    </DocumentPage>
  );
}
